import React, { useEffect, useState } from 'react';
import { Space, Tabs } from '@mantine/core';
import axios from 'axios';
import { shallow } from 'zustand/shallow';
import toast from 'react-hot-toast';

import Keywords from './Keywords';
import Checkins from './Checkins';
import GeneralSettings from './GeneralSettings';
import InitialEntry from './InitialEntry';
import WinnerRules from './WinnerRules';
import ContestSettingsLibrary from '../ContestSettingsLibrary';
import { ErrorView } from '@components/shared';

import useAppStore from './contest-settings-store';

const tabDefs = {
  general: 'general',
  keywords: 'keywords',
  initial_entry: 'initial entry',
  check_ins: 'check-ins',
  winner_rules: 'winner rules',
};

export default function ContestSettings({
  beginDate,
  canEditWinnerRules,
  contestId,
  endDate,
  fetchContest,
  wizardSettings,
  contestVariety = null,
  contestInfo = null,
}) {
  const [error, setError] = useState(null);
  const { settings, setSettings, reset } = useAppStore(
    (state) => ({
      settings: state.settings,
      setSettings: state.setSettings,
      reset: state.reset,
    }),
    shallow
  );

  useEffect(() => {
    reset();
    fetchSettings();

    return () => {
      reset();
    };
  }, [contestId]);

  function fetchSettings() {
    axios
      .get(`/contests/${contestId}/settings/`)
      .then(({ data }) => {
        if (
          data.response &&
          data.response.length > 0 &&
          data.response[0].settings
        ) {
          // If there are settings, set the settings
          setSettings({
            ...settings,
            ...data.response[0].settings,
            contestDates: {
              begin: beginDate,
              end: endDate,
            },
          });
        } else {
          // If there are no settings, set the default settings
          setSettings({
            ...settings,
            contestDates: {
              begin: beginDate,
              end: endDate,
            },
          });
        }
      })
      .catch((err) => {
        // setError(err);
        setError(
          'Something went wrong while fetching the settings. Please reload the page and try again.'
        );
      });
  }

  function updateSettings(newSettings) {
    const req = { contest_id: contestId, settings: newSettings };

    axios
      .post(`/contests/${contestId}/settings/`, req)
      .then(() => {
        fetchSettings();
      })
      .then(() => fetchContest())
      .catch((err) => {
        toast.error(err);
      });
  }

  function onUpdate(newSettings) {
    updateSettings({ ...settings, ...newSettings });
  }

  const isTieredContest = contestInfo?.variety === 4;

  if (error) {
    return <ErrorView text={error} />;
  }

  return (
    <div>
      <ContestSettingsLibrary
        createReqData={{
          contest_id: contestId,
          template_data: settings,
        }}
        addReqData={{
          contest_id: contestId,
        }}
        fetchData={fetchSettings}
      />
      <Space mb="lg" />
      <Tabs
        onChange={(e) => onUpdate({ activeTab: e })}
        value={settings.activeTab}
      >
        <Tabs.List mb="xl">
          <Tabs.Tab value={tabDefs.general}>General</Tabs.Tab>
          {!isTieredContest && (
            <Tabs.Tab value={tabDefs.keywords}>Keywords</Tabs.Tab>
          )}
          <Tabs.Tab value={tabDefs.initial_entry}>Initial Entry</Tabs.Tab>
          <Tabs.Tab value={tabDefs.check_ins}>Check-Ins</Tabs.Tab>
          <Tabs.Tab value={tabDefs.winner_rules}>Winner Rules</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={tabDefs.general}>
          <GeneralSettings
            onUpdate={(e) => onUpdate({ ...e })}
            contestVariety={contestVariety}
          />
        </Tabs.Panel>
        {!isTieredContest && (
          <Tabs.Panel value={tabDefs.keywords}>
            <Keywords
              beginDate={beginDate}
              endDate={endDate}
              onUpdate={(e) =>
                onUpdate({ keywords: { ...settings.keywords, ...e } })
              }
            />
          </Tabs.Panel>
        )}
        <Tabs.Panel value={tabDefs.initial_entry}>
          <InitialEntry
            onUpdate={(e) =>
              onUpdate({ initial_entry: { ...settings.initial_entry, ...e } })
            }
          />
        </Tabs.Panel>
        <Tabs.Panel value={tabDefs.check_ins}>
          <Checkins
            beginDate={beginDate}
            endDate={endDate}
            onUpdate={(e) =>
              onUpdate({ check_ins: { ...settings.check_ins, ...e } })
            }
          />
        </Tabs.Panel>
        <Tabs.Panel value={tabDefs.winner_rules}>
          <WinnerRules
            canEditWinnerRules={canEditWinnerRules}
            onUpdate={(e) =>
              onUpdate({ winner_rules: { ...settings.winner_rules, ...e } })
            }
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
